import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoopIcon from '@mui/icons-material/Loop';
import { Box, Slide } from '@mui/material';
import { ERROR_STATUS_MESSAGE, SUCCESS_STATUS_MESSAGE, WARNING_STATUS_MESSAGE } from 'constant';
import { actions as globalStateActions, useGlobalState } from 'context/GlobalState/GlobalStateContext';
import {
  ALERT_CLOSE_ANIMATION_DURATION_MS,
  TOAST_MAX_VISIBLE,
  TOAST_STACK_HOVER_MIN_COUNT,
} from 'components/Alert/constants';
import {
  StackedErrorAlert,
  StackedSuccessAlert,
  StackedWarningAlert,
  StyledErrorAlert,
  StyledSuccessAlert,
  StyledWarningAlert,
  ToastCollapsedItem,
  ToastExpandedStack,
  ToastStackContainer,
  ToastViewport,
} from 'components/Alert/styled';
import { AlertState } from 'context/GlobalState/types';
import { animations } from 'themes';

const LEGACY_DISPLAY_MODE = 'full-width-banner';

const SimpleAlert: FC = () => {
  const {
    state: { alertDetails },
    dispatch,
  } = useGlobalState();
  const [isToastExpanded, setIsToastExpanded] = useState(false);

  const clearPopup = useCallback(
    (alertId: string) => {
      dispatch(globalStateActions?.closeAlert({ id: alertId }));

      setTimeout(() => {
        dispatch(globalStateActions?.clearAlert({ id: alertId }));
      }, ALERT_CLOSE_ANIMATION_DURATION_MS);
    },
    [dispatch],
  );

  const legacyAlerts = useMemo(
    () => alertDetails.filter((alert) => (alert.displayMode || LEGACY_DISPLAY_MODE) !== 'stacked-toast'),
    [alertDetails],
  );

  const toastAlerts = useMemo(
    () =>
      alertDetails
        .filter((alert) => (alert.displayMode || LEGACY_DISPLAY_MODE) === 'stacked-toast')
        .slice(-TOAST_MAX_VISIBLE)
        .reverse(),
    [alertDetails],
  );

  const canExpandToastStack = toastAlerts.length >= TOAST_STACK_HOVER_MIN_COUNT;
  const isToastStackExpanded = canExpandToastStack && isToastExpanded;

  useEffect(() => {
    if (!canExpandToastStack && isToastExpanded) {
      setIsToastExpanded(false);
    }
  }, [canExpandToastStack, isToastExpanded]);

  const renderLegacyAlert = useCallback(
    (alert: AlertState) => {
      if (alert.type === SUCCESS_STATUS_MESSAGE) {
        return (
          <Slide direction='down' timeout={600} in={alert.isOpen} mountOnEnter unmountOnExit>
            <StyledSuccessAlert
              iconMapping={{ success: <CheckCircleIcon htmlColor='#fff' fontSize='inherit' /> }}
              onClose={() => clearPopup(alert.id)}
              drawerOpen={false}
            >
              {alert.message}
            </StyledSuccessAlert>
          </Slide>
        );
      }

      if (alert.type === WARNING_STATUS_MESSAGE) {
        return (
          <Slide direction='down' timeout={600} in={alert.isOpen} mountOnEnter unmountOnExit>
            <StyledWarningAlert
              iconMapping={{
                warning: (
                  <LoopIcon
                    htmlColor='#fff'
                    fontSize='inherit'
                    sx={{ animation: `${animations.spin} 2s linear infinite` }}
                  />
                ),
              }}
              severity={WARNING_STATUS_MESSAGE}
              onClose={() => clearPopup(alert.id)}
              drawerOpen={false}
            >
              {alert.message}
            </StyledWarningAlert>
          </Slide>
        );
      }

      return (
        <Slide direction='down' timeout={600} in={alert.isOpen} mountOnEnter unmountOnExit>
          <StyledErrorAlert
            iconMapping={{ error: <CancelIcon htmlColor='#fff' fontSize='inherit' /> }}
            severity={ERROR_STATUS_MESSAGE}
            onClose={() => clearPopup(alert.id)}
            drawerOpen={false}
          >
            {alert.message}
          </StyledErrorAlert>
        </Slide>
      );
    },
    [clearPopup],
  );

  const renderStackedAlert = useCallback(
    (alert: AlertState) => {
      if (alert.type === SUCCESS_STATUS_MESSAGE) {
        return (
          <StackedSuccessAlert
            iconMapping={{ success: <CheckCircleIcon fontSize='inherit' /> }}
            severity={SUCCESS_STATUS_MESSAGE}
            onClose={() => clearPopup(alert.id)}
          >
            {alert.message}
          </StackedSuccessAlert>
        );
      }

      if (alert.type === WARNING_STATUS_MESSAGE) {
        return (
          <StackedWarningAlert
            iconMapping={{
              warning: (
                <LoopIcon
                  fontSize='inherit'
                  sx={{ animation: `${animations.spin} 2s linear infinite` }}
                />
              ),
            }}
            severity={WARNING_STATUS_MESSAGE}
            onClose={() => clearPopup(alert.id)}
          >
            {alert.message}
          </StackedWarningAlert>
        );
      }

      return (
        <StackedErrorAlert
          iconMapping={{ error: <CancelIcon fontSize='inherit' /> }}
          severity={ERROR_STATUS_MESSAGE}
          onClose={() => clearPopup(alert.id)}
        >
          {alert.message}
        </StackedErrorAlert>
      );
    },
    [clearPopup],
  );

  return (
    <>
      {legacyAlerts.map((alert) => (
        <React.Fragment key={`legacy-alert-popup-key-${alert.id}`}>{renderLegacyAlert(alert)}</React.Fragment>
      ))}

      <ToastViewport>
        {!!toastAlerts.length && (
          <ToastStackContainer
            onMouseEnter={canExpandToastStack ? () => setIsToastExpanded(true) : undefined}
            onMouseLeave={canExpandToastStack ? () => setIsToastExpanded(false) : undefined}
            expanded={isToastStackExpanded}
          >
            {isToastStackExpanded ? (
              <ToastExpandedStack>
                {toastAlerts.map((alert) => (
                  <Slide key={`toast-expanded-alert-key-${alert.id}`} direction='down' timeout={300} in={alert.isOpen} mountOnEnter unmountOnExit>
                    <Box sx={{ width: '100%' }}>{renderStackedAlert(alert)}</Box>
                  </Slide>
                ))}
              </ToastExpandedStack>
            ) : (
              toastAlerts.map((alert, index) => (
                <ToastCollapsedItem key={`toast-collapsed-alert-key-${alert.id}`} stackIndex={index}>
                  <Slide direction='down' timeout={300} in={alert.isOpen} mountOnEnter unmountOnExit>
                    <Box sx={{ width: '100%' }}>{renderStackedAlert(alert)}</Box>
                  </Slide>
                </ToastCollapsedItem>
              ))
            )}
          </ToastStackContainer>
        )}
      </ToastViewport>
    </>
  );
};

export default memo(SimpleAlert);
